import React, { useState, useEffect } from 'react';
import { Save, GripVertical, Eye, EyeOff, ChevronUp, ChevronDown, Loader2, Home, Menu as MenuIcon } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AVAILABLE_PAGES = [
  { slug: 'entradas', label: 'Comprar Entradas' },
  { slug: 'presentacion', label: 'Presentacion' },
  { slug: 'sinopsis', label: 'Sinopsis' },
  { slug: 'trailer', label: 'Trailer' },
  { slug: 'ficha', label: 'Ficha' },
  { slug: 'sobre-la-pelicula', label: 'Sobre la Pelicula' },
];

const AdminSiteConfig = () => {
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const token = localStorage.getItem('admin_token');

  useEffect(() => { fetchConfig(); }, []);

  const fetchConfig = async () => {
    try {
      const res = await fetch(`${API_URL}/api/site-config`);
      setConfig(await res.json());
    } catch (e) { console.error(e); }
  };

  const saveConfig = async () => {
    if (!config) return;
    setSaving(true);
    setMessage('');
    try {
      const res = await fetch(`${API_URL}/api/site-config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          homePage: config.homePage,
          menuItems: config.menuItems,
        }),
      });
      if (res.ok) {
        setMessage('Configuracion guardada');
        fetchConfig();
      } else {
        setMessage('Error al guardar');
      }
    } catch (e) {
      setMessage('Error de conexion');
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const toggleVisibility = (index) => {
    const items = [...config.menuItems];
    items[index] = { ...items[index], visible: !items[index].visible };
    setConfig({ ...config, menuItems: items });
  };

  const updateLabel = (index, label) => {
    const items = [...config.menuItems];
    items[index] = { ...items[index], label };
    setConfig({ ...config, menuItems: items });
  };

  const moveItem = (index, direction) => {
    const items = [...config.menuItems];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= items.length) return;
    [items[index], items[newIndex]] = [items[newIndex], items[index]];
    items.forEach((item, i) => item.order = i);
    setConfig({ ...config, menuItems: items });
  };

  if (!config) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin w-8 h-8 border-4 border-[#50c878] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div data-testid="admin-site-config">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Configuracion del Sitio</h1>
        {message && (
          <span className={`text-sm px-3 py-1 rounded-full ${message.includes('Error') ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'}`}>
            {message}
          </span>
        )}
      </div>

      <div className="space-y-8">
        {/* Homepage Selector */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center gap-3 mb-4">
            <Home className="w-5 h-5 text-[#50c878]" />
            <h2 className="text-lg font-semibold text-gray-800">Pagina de Inicio</h2>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            Elige que pagina se mostrara cuando los visitantes accedan a la web.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {AVAILABLE_PAGES.map(p => (
              <button
                key={p.slug}
                onClick={() => setConfig({ ...config, homePage: p.slug })}
                className={`px-4 py-3 rounded-lg text-sm font-medium border-2 transition-all ${
                  config.homePage === p.slug
                    ? 'border-[#50c878] bg-[#50c878]/10 text-[#50c878]'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
                data-testid={`homepage-option-${p.slug}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Menu Items Editor */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center gap-3 mb-4">
            <MenuIcon className="w-5 h-5 text-[#50c878]" />
            <h2 className="text-lg font-semibold text-gray-800">Menu de Navegacion</h2>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            Configura los elementos del menu, su orden, nombre y visibilidad.
          </p>

          <div className="space-y-2">
            {config.menuItems.map((item, index) => (
              <div
                key={item.slug}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg border transition-colors ${
                  item.visible ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-100'
                }`}
                data-testid={`menu-item-${item.slug}`}
              >
                <GripVertical className="w-4 h-4 text-gray-400 flex-shrink-0" />
                
                <input
                  type="text"
                  value={item.label}
                  onChange={e => updateLabel(index, e.target.value)}
                  className={`flex-1 px-2 py-1 border border-transparent rounded text-sm font-medium focus:border-gray-300 focus:outline-none ${
                    item.visible ? 'text-gray-800' : 'text-gray-400'
                  }`}
                  data-testid={`menu-label-${item.slug}`}
                />

                <span className="text-xs text-gray-400 font-mono">{item.slug}</span>

                <div className="flex items-center gap-1">
                  <button onClick={() => moveItem(index, -1)} disabled={index === 0}
                    className="p-1 hover:bg-gray-100 rounded disabled:opacity-30">
                    <ChevronUp className="w-4 h-4 text-gray-500" />
                  </button>
                  <button onClick={() => moveItem(index, 1)} disabled={index === config.menuItems.length - 1}
                    className="p-1 hover:bg-gray-100 rounded disabled:opacity-30">
                    <ChevronDown className="w-4 h-4 text-gray-500" />
                  </button>
                  <button
                    onClick={() => toggleVisibility(index)}
                    className={`p-1.5 rounded transition-colors ${item.visible ? 'hover:bg-gray-100 text-[#50c878]' : 'hover:bg-gray-100 text-gray-400'}`}
                    data-testid={`toggle-visibility-${item.slug}`}
                  >
                    {item.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Save Button */}
        <button
          onClick={saveConfig}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-3 bg-[#50c878] text-white rounded-lg hover:bg-[#40a0a0] transition-colors disabled:opacity-50"
          data-testid="save-config-button"
        >
          {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
          {saving ? 'Guardando...' : 'Guardar Configuracion'}
        </button>
      </div>
    </div>
  );
};

export default AdminSiteConfig;
